import type { DigestFrequency } from "./types";

/** Canonical digest cadence ids: daily | weekly | biweekly. */
export const digestFrequencies: {
  value: DigestFrequency;
  label: string;
  hint: string;
  /** False = available on trial / unpaid. */
  requiresPaid: boolean;
}[] = [
  {
    value: "daily",
    label: "Daily",
    hint: "Each weekday morning, at the time you choose",
    requiresPaid: true,
  },
  {
    value: "weekly",
    label: "Weekly",
    hint: "Default. Free week sends weekly; stays available when you continue",
    requiresPaid: false,
  },
  {
    value: "biweekly",
    label: "Bi-weekly",
    hint: "Every two weeks, at the time you choose",
    requiresPaid: true,
  },
];

/** Paid plan unlocks daily and bi-weekly. Trial / expired / unpaid: weekly only. */
export function frequenciesUnlocked(planStatus: string | undefined | null): boolean {
  return String(planStatus ?? "").toLowerCase() === "paid";
}

/** Map bi-weekly / monthly (legacy) → biweekly. */
export function normalizeDigestFrequency(raw: string | undefined | null): DigestFrequency {
  const s = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_-]/g, "");
  if (s === "daily") return "daily";
  if (s === "biweekly" || s === "monthly") return "biweekly";
  return "weekly";
}


export const timezones = [
  { value: "Europe/Copenhagen", label: "Copenhagen" },
  { value: "Europe/London", label: "London" },
  { value: "Europe/Dublin", label: "Dublin" },
  { value: "Europe/Amsterdam", label: "Amsterdam" },
  { value: "Europe/Berlin", label: "Berlin" },
  { value: "Europe/Paris", label: "Paris" },
  { value: "Europe/Stockholm", label: "Stockholm" },
  { value: "Europe/Oslo", label: "Oslo" },
  { value: "UTC", label: "UTC" },
] as const;

export const sendTimes = ["06:00", "07:00", "08:00", "09:00", "12:00", "17:00"] as const;

export const personas = [
  {
    value: "in-house" as const,
    label: "Associated with the team responsible for horizon scanning and tracking",
  },
  {
    value: "advisor" as const,
    label: "An advisor to regulated organisations",
  },
  {
    value: "vendor" as const,
    label: "A vendor selling products or services to regulated organisations",
  },
  {
    value: "other" as const,
    label: "Acting in another capacity",
  },
];

/** Maintained blocklist of free and disposable mailbox providers. */
export const freeEmailDomains = new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "yahoo.co.uk",
  "hotmail.com",
  "hotmail.co.uk",
  "outlook.com",
  "live.com",
  "msn.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "proton.me",
  "protonmail.com",
  "pm.me",
  "gmx.com",
  "gmx.net",
  "mail.com",
  "zoho.com",
  "yandex.com",
  "fastmail.com",
  "tutanota.com",
  "mailinator.com",
  "guerrillamail.com",
  "10minutemail.com",
  "tempmail.com",
  "yopmail.com",
]);

export function isWorkEmail(email: string): boolean {
  const at = email.trim().toLowerCase().lastIndexOf("@");
  if (at < 1) return false;
  const domain = email.trim().toLowerCase().slice(at + 1);
  if (!domain.includes(".") || domain.endsWith(".")) return false;
  return !freeEmailDomains.has(domain);
}

export const SOURCE_REQUEST_TURNAROUND = "We review source requests within 10 working days.";
export const FREE_TOPIC_LIMIT = 1;
export const RECIPIENT_CAP = 10;
/** €29 × source count / month (licensed). Contact page until HorizonScanning:StripePriceId is set. */
export const SOURCE_MONTHLY_EUR = 29;
/** Free week of weekly digests before contact to continue. */
export const TRIAL_DAYS = 7;
